import React, { useState, useMemo, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { vehiclesService } from '../services/firestoreService';
import { formatCurrency } from '../utils/helpers';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/** Vehicles in Archive that can be sent back to the garage */
const isRestorableVehicle = (v) =>
  !!v?.id && (!!v.deletedAt || v.status === 'Archive' || (!!v.paid && !!v.collected));

const Archive = ({ onOpenVehicleDetail }) => {
  const { vehicles, refreshVehicles } = useApp();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [restoringIds, setRestoringIds] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isRestoreMode, setIsRestoreMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const handleBulkRestore = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setRestoringIds(new Set(ids));
    const failed = [];
    try {
      for (const id of ids) {
        try {
          await vehiclesService.restore(id);
        } catch (err) {
          console.error('Error restoring vehicle', id, err);
          failed.push(id);
        }
      }
      await refreshVehicles();
      setSelectedIds(new Set());
      setSelectedVehicle(null);
      setIsRestoreMode(false);
      if (failed.length > 0) {
        window.alert(`Restored ${ids.length - failed.length} of ${ids.length} vehicles. Some failed — please try again.`);
      } else {
        window.alert(
          ids.length === 1
            ? 'Vehicle has been restored to the garage.'
            : `${ids.length} vehicles have been restored to the garage.`
        );
      }
    } catch (err) {
      console.error('Error restoring vehicles:', err);
      window.alert(`Failed to restore some vehicles. ${err?.message || 'Please try again.'}`);
    } finally {
      setRestoringIds(null);
    }
  }, [selectedIds, refreshVehicles]);

  const toggleSelect = useCallback((id) => {
    if (!id) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const isRestoringSelected = restoringIds && restoringIds.size > 0;

  const archiveVehicles = useMemo(() => {
    return vehicles
      .filter(veh => veh.deletedAt || veh.status === 'Archive' || veh.collected || (veh.paid && veh.collected))
      .sort((a, b) => {
        const dateA = a.collectionDate ? new Date(a.collectionDate) : new Date(0);
        const dateB = b.collectionDate ? new Date(b.collectionDate) : new Date(0);
        return dateB - dateA;
      });
  }, [vehicles]);

  // Filter vehicles by status for Sales and Workshop
  const salesVehicles = useMemo(() => {
    return vehicles
      .filter(veh => veh.status === 'Sales')
      .sort((a, b) => {
        const dateA = a.dropOffDate || a.receivedDate ? new Date(a.dropOffDate || a.receivedDate) : new Date(0);
        const dateB = b.dropOffDate || b.receivedDate ? new Date(b.dropOffDate || b.receivedDate) : new Date(0);
        return dateB - dateA;
      });
  }, [vehicles]);

  const workshopVehicles = useMemo(() => {
    return vehicles
      .filter(veh => veh.status === 'Workshop' || veh.status === 'MOT' || veh.status === 'Bodyshop' || veh.status === 'Tyres')
      .sort((a, b) => {
        const dateA = a.dropOffDate || a.receivedDate ? new Date(a.dropOffDate || a.receivedDate) : new Date(0);
        const dateB = b.dropOffDate || b.receivedDate ? new Date(b.dropOffDate || b.receivedDate) : new Date(0);
        return dateB - dateA;
      });
  }, [vehicles]);

  // Calculate archive statistics
  const archiveStats = useMemo(() => {
    const totalVehicles = archiveVehicles.length;
    const totalValue = archiveVehicles.reduce((sum, veh) => sum + (veh.cost || 0), 0);
    const paidVehicles = archiveVehicles.filter(veh => veh.paid);
    const unpaidVehicles = archiveVehicles.filter(veh => !veh.paid);
    const averageValue = totalVehicles > 0 ? totalValue / totalVehicles : 0;
    const totalPaidValue = paidVehicles.reduce((sum, veh) => sum + (veh.cost || 0), 0);
    const totalUnpaidValue = unpaidVehicles.reduce((sum, veh) => sum + (veh.cost || 0), 0);

    return {
      totalVehicles,
      totalValue,
      paidVehicles,
      unpaidVehicles,
      averageValue,
      totalPaidValue,
      totalUnpaidValue
    };
  }, [archiveVehicles]);

  // Filter vehicles based on search term and active tab
  const filteredVehicles = useMemo(() => {
    let vehiclesToFilter = archiveVehicles;
    
    if (activeTab === 'sales') {
      vehiclesToFilter = salesVehicles;
    } else if (activeTab === 'workshop') {
      vehiclesToFilter = workshopVehicles;
    }
    
    if (!searchTerm) return vehiclesToFilter;
    const query = searchTerm.toLowerCase();
    return vehiclesToFilter.filter(veh => 
      veh.reg?.toLowerCase().includes(query) ||
      veh.vehicle?.toLowerCase().includes(query) ||
      veh.contact?.toLowerCase().includes(query) ||
      veh.summary?.toLowerCase().includes(query)
    );
  }, [archiveVehicles, salesVehicles, workshopVehicles, activeTab, searchTerm]);

  const restorableVehicles = useMemo(() => {
    const list = activeTab === 'stored' ? archiveVehicles : filteredVehicles;
    return list.filter(isRestorableVehicle);
  }, [activeTab, filteredVehicles, archiveVehicles]);

  const selectAllRestorable = useCallback(() => {
    setSelectedIds(new Set(restorableVehicles.map(v => v.id)));
  }, [restorableVehicles]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleVehicleClick = (veh) => {
    if (onOpenVehicleDetail) {
      const vehicleIndex = vehicles.findIndex(v => v.reg === veh.reg);
      if (vehicleIndex !== -1) {
        onOpenVehicleDetail(vehicleIndex);
      }
    }
  };

  const renderOverview = () => (
    <div>
      <div className="report-summary">
        <div className="report-summary-item">
          <div className="report-summary-label">Total Vehicles</div>
          <div className="report-summary-value">{archiveStats.totalVehicles}</div>
        </div>
        <div className="report-summary-item">
          <div className="report-summary-label">Total Value</div>
          <div className="report-summary-value">{formatCurrency(archiveStats.totalValue)}</div>
        </div>
        <div className="report-summary-item">
          <div className="report-summary-label">Paid Value</div>
          <div className="report-summary-value">{formatCurrency(archiveStats.totalPaidValue)}</div>
        </div>
        <div className="report-summary-item">
          <div className="report-summary-label">Average Value</div>
          <div className="report-summary-value">{formatCurrency(archiveStats.averageValue)}</div>
        </div>
      </div>
      
      <div className="vehicle-grid">
        {filteredVehicles.length === 0 ? (
          <div className="empty-state">No vehicles found.</div>
        ) : (
          filteredVehicles.map((vehicle, index) => {
            const tags = [];
            if (vehicle.paid) {
              tags.push(
                <span key="paid" className="vehicle-tag vehicle-tag--success">Paid</span>
              );
            } else {
              tags.push(
                <span key="unpaid" className="vehicle-tag vehicle-tag--danger">Unpaid</span>
              );
            }
            if (vehicle.collected) {
              tags.push(
                <span key="collected" className="vehicle-tag vehicle-tag--success">Collected</span>
              );
            }
            if (vehicle.deletedAt) {
              tags.push(
                <span key="deleted" className="vehicle-tag" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#b91c1c' }}>Deleted</span>
              );
            }
            
            const isSelected = selectedVehicle && (selectedVehicle.id === vehicle.id || (selectedVehicle.reg === vehicle.reg && !vehicle.id));
            const isBulkSelected = isRestorableVehicle(vehicle) && selectedIds.has(vehicle.id);
            const showBulkCheckbox = isRestoreMode && isRestorableVehicle(vehicle);
            return (
              <div
                key={index}
                className={`vehicle-card${isSelected ? ' vehicle-card--selected' : ''}${isBulkSelected ? ' vehicle-card--bulk-selected' : ''}`}
                style={{ position: 'relative', cursor: 'pointer' }}
                onClick={() => {
                  if (isRestoreMode && isRestorableVehicle(vehicle)) {
                    toggleSelect(vehicle.id);
                  } else if (!isRestoreMode) {
                    setSelectedVehicle(vehicle);
                    if (!vehicle.deletedAt) handleVehicleClick(vehicle);
                  }
                }}
              >
                {showBulkCheckbox && (
                  <span
                    className="vehicle-card-checkbox"
                    onClick={(e) => { e.stopPropagation(); toggleSelect(vehicle.id); }}
                    role="checkbox"
                    aria-checked={isBulkSelected}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '18px',
                      height: '18px',
                      border: '2px solid var(--border)',
                      borderRadius: '4px',
                      background: isBulkSelected ? 'var(--primary)' : 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {isBulkSelected && <span style={{ color: '#fff', fontSize: '12px' }}>✓</span>}
                  </span>
                )}
                <span className="vehicle-card-reg">{vehicle.reg || 'N/A'}</span>
                <div className="vehicle-card-title">{vehicle.vehicle || 'Unknown Vehicle'}</div>
                <div className="vehicle-card-meta">
                  <span>{formatDate(vehicle.collectionDate)}</span>
                  <span>{formatCurrency(vehicle.cost || 0)}</span>
                </div>
                {tags.length > 0 && (
                  <div className="vehicle-card-tags">
                    {tags}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const renderStoredVehicles = () => (
    <div>
      <div className="report-summary">
        <div className="report-summary-item">
          <div className="report-summary-label">Stored Vehicles</div>
          <div className="report-summary-value">{archiveStats.totalVehicles}</div>
        </div>
        <div className="report-summary-item">
          <div className="report-summary-label">Total Storage Value</div>
          <div className="report-summary-value">{formatCurrency(archiveStats.totalValue)}</div>
        </div>
      </div>
      
      <div className="vehicle-grid">
        {archiveVehicles.length === 0 ? (
          <div className="empty-state">No stored vehicles found.</div>
        ) : (
          archiveVehicles.map((vehicle, index) => {
            const tags = [];
            if (vehicle.paid) {
              tags.push(
                <span key="paid" className="vehicle-tag vehicle-tag--success">Paid</span>
              );
            } else {
              tags.push(
                <span key="unpaid" className="vehicle-tag vehicle-tag--danger">Unpaid</span>
              );
            }
            tags.push(
              <span key="stored" className="vehicle-tag">Stored</span>
            );
            if (vehicle.deletedAt) {
              tags.push(
                <span key="deleted" className="vehicle-tag" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#b91c1c' }}>Deleted</span>
              );
            }
            
            const isSelected = selectedVehicle && (selectedVehicle.id === vehicle.id || (selectedVehicle.reg === vehicle.reg && !vehicle.id));
            const isBulkSelected = isRestorableVehicle(vehicle) && selectedIds.has(vehicle.id);
            const showBulkCheckbox = isRestoreMode && isRestorableVehicle(vehicle);
            return (
              <div
                key={index}
                className={`vehicle-card${isSelected ? ' vehicle-card--selected' : ''}${isBulkSelected ? ' vehicle-card--bulk-selected' : ''}`}
                style={{ position: 'relative', cursor: 'pointer' }}
                onClick={() => {
                  if (isRestoreMode && isRestorableVehicle(vehicle)) {
                    toggleSelect(vehicle.id);
                  } else if (!isRestoreMode) {
                    setSelectedVehicle(vehicle);
                    if (!vehicle.deletedAt) handleVehicleClick(vehicle);
                  }
                }}
              >
                {showBulkCheckbox && (
                  <span
                    className="vehicle-card-checkbox"
                    onClick={(e) => { e.stopPropagation(); toggleSelect(vehicle.id); }}
                    role="checkbox"
                    aria-checked={isBulkSelected}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '18px',
                      height: '18px',
                      border: '2px solid var(--border)',
                      borderRadius: '4px',
                      background: isBulkSelected ? 'var(--primary)' : 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {isBulkSelected && <span style={{ color: '#fff', fontSize: '12px' }}>✓</span>}
                  </span>
                )}
                <span className="vehicle-card-reg">{vehicle.reg || 'N/A'}</span>
                <div className="vehicle-card-title">{vehicle.vehicle || 'Unknown Vehicle'}</div>
                <div className="vehicle-card-meta">
                  <span>Stored</span>
                  <span>{formatDate(vehicle.collectionDate)}</span>
                  <span>{formatCurrency(vehicle.cost || 0)}</span>
                </div>
                {tags.length > 0 && (
                  <div className="vehicle-card-tags">
                    {tags}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const renderSalesVehicles = () => {
    const salesStats = {
      totalVehicles: salesVehicles.length,
      totalValue: salesVehicles.reduce((sum, veh) => sum + (veh.salePrice || 0), 0),
      totalCost: salesVehicles.reduce((sum, veh) => sum + ((veh.cost || 0) + (veh.prepCost || 0) + (veh.warranty || 0) + (veh.misc || 0)), 0)
    };

    return (
      <div>
        <div className="report-summary">
          <div className="report-summary-item">
            <div className="report-summary-label">Sales Vehicles</div>
            <div className="report-summary-value">{salesStats.totalVehicles}</div>
          </div>
          <div className="report-summary-item">
            <div className="report-summary-label">Total Sale Value</div>
            <div className="report-summary-value">{formatCurrency(salesStats.totalValue)}</div>
          </div>
          <div className="report-summary-item">
            <div className="report-summary-label">Total Cost</div>
            <div className="report-summary-value">{formatCurrency(salesStats.totalCost)}</div>
          </div>
          <div className="report-summary-item">
            <div className="report-summary-label">Total Profit</div>
            <div className="report-summary-value">{formatCurrency(salesStats.totalValue - salesStats.totalCost)}</div>
          </div>
        </div>
        
        <div className="vehicle-grid">
          {filteredVehicles.length === 0 ? (
            <div className="empty-state">No sales vehicles found.</div>
          ) : (
            filteredVehicles.map((vehicle, index) => {
              const tags = [];
              if (vehicle.paid) {
                tags.push(
                  <span key="paid" className="vehicle-tag vehicle-tag--success">Paid</span>
                );
              } else {
                tags.push(
                  <span key="unpaid" className="vehicle-tag vehicle-tag--danger">Unpaid</span>
                );
              }
              tags.push(
                <span key="sales" className="vehicle-tag">Sales</span>
              );
              if (vehicle.deletedAt) {
                tags.push(
                  <span key="deleted" className="vehicle-tag" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#b91c1c' }}>Deleted</span>
                );
              }
              
              const isBulkSelected = isRestorableVehicle(vehicle) && selectedIds.has(vehicle.id);
              const showBulkCheckbox = isRestoreMode && isRestorableVehicle(vehicle);
              return (
                <div
                  key={index}
                  className={`vehicle-card${isBulkSelected ? ' vehicle-card--bulk-selected' : ''}`}
                  style={{ position: 'relative', cursor: 'pointer' }}
                  onClick={() => {
                    if (isRestoreMode && isRestorableVehicle(vehicle)) {
                      toggleSelect(vehicle.id);
                    } else if (!isRestoreMode) {
                      handleVehicleClick(vehicle);
                    }
                  }}
                >
                  {showBulkCheckbox && (
                    <span
                      className="vehicle-card-checkbox"
                      onClick={(e) => { e.stopPropagation(); toggleSelect(vehicle.id); }}
                      role="checkbox"
                      aria-checked={isBulkSelected}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '18px',
                        height: '18px',
                        border: '2px solid var(--border)',
                        borderRadius: '4px',
                        background: isBulkSelected ? 'var(--primary)' : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {isBulkSelected && <span style={{ color: '#fff', fontSize: '12px' }}>✓</span>}
                    </span>
                  )}
                  <span className="vehicle-card-reg">{vehicle.reg || 'N/A'}</span>
                  <div className="vehicle-card-title">{vehicle.vehicle || 'Unknown Vehicle'}</div>
                  <div className="vehicle-card-meta">
                    <span>Sales</span>
                    <span>{formatDate(vehicle.dropOffDate || vehicle.receivedDate)}</span>
                    <span>{formatCurrency(vehicle.salePrice || 0)}</span>
                  </div>
                  {tags.length > 0 && (
                    <div className="vehicle-card-tags">
                      {tags}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderWorkshopVehicles = () => {
    const workshopStats = {
      totalVehicles: workshopVehicles.length,
      totalValue: workshopVehicles.reduce((sum, veh) => sum + (veh.cost || 0), 0),
      averageValue: workshopVehicles.length > 0 
        ? workshopVehicles.reduce((sum, veh) => sum + (veh.cost || 0), 0) / workshopVehicles.length 
        : 0
    };

    return (
      <div>
        <div className="report-summary">
          <div className="report-summary-item">
            <div className="report-summary-label">Workshop Vehicles (MOT, Bodyshop, Workshop, Tyres)</div>
            <div className="report-summary-value">{workshopStats.totalVehicles}</div>
          </div>
          <div className="report-summary-item">
            <div className="report-summary-label">Total Value</div>
            <div className="report-summary-value">{formatCurrency(workshopStats.totalValue)}</div>
          </div>
          <div className="report-summary-item">
            <div className="report-summary-label">Average Value</div>
            <div className="report-summary-value">{formatCurrency(workshopStats.averageValue)}</div>
          </div>
        </div>
        
        <div className="vehicle-grid">
          {filteredVehicles.length === 0 ? (
            <div className="empty-state">No workshop vehicles found.</div>
          ) : (
            filteredVehicles.map((vehicle, index) => {
              const tags = [];
              if (vehicle.paid) {
                tags.push(
                  <span key="paid" className="vehicle-tag vehicle-tag--success">Paid</span>
                );
              } else {
                tags.push(
                  <span key="unpaid" className="vehicle-tag vehicle-tag--danger">Unpaid</span>
                );
              }
              tags.push(
                <span key="status" className="vehicle-tag">{vehicle.status || 'Workshop'}</span>
              );
              if (vehicle.deletedAt) {
                tags.push(
                  <span key="deleted" className="vehicle-tag" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#b91c1c' }}>Deleted</span>
                );
              }
              
              const isBulkSelected = isRestorableVehicle(vehicle) && selectedIds.has(vehicle.id);
              const showBulkCheckbox = isRestoreMode && isRestorableVehicle(vehicle);
              return (
                <div
                  key={index}
                  className={`vehicle-card${isBulkSelected ? ' vehicle-card--bulk-selected' : ''}`}
                  style={{ position: 'relative', cursor: 'pointer' }}
                  onClick={() => {
                    if (isRestoreMode && isRestorableVehicle(vehicle)) {
                      toggleSelect(vehicle.id);
                    } else if (!isRestoreMode) {
                      handleVehicleClick(vehicle);
                    }
                  }}
                >
                  {showBulkCheckbox && (
                    <span
                      className="vehicle-card-checkbox"
                      onClick={(e) => { e.stopPropagation(); toggleSelect(vehicle.id); }}
                      role="checkbox"
                      aria-checked={isBulkSelected}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '18px',
                        height: '18px',
                        border: '2px solid var(--border)',
                        borderRadius: '4px',
                        background: isBulkSelected ? 'var(--primary)' : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {isBulkSelected && <span style={{ color: '#fff', fontSize: '12px' }}>✓</span>}
                    </span>
                  )}
                  <span className="vehicle-card-reg">{vehicle.reg || 'N/A'}</span>
                  <div className="vehicle-card-title">{vehicle.vehicle || 'Unknown Vehicle'}</div>
                  <div className="vehicle-card-meta">
                    <span>{vehicle.status || 'Workshop'}</span>
                    <span>{formatDate(vehicle.dropOffDate || vehicle.receivedDate)}</span>
                    <span>{formatCurrency(vehicle.cost || 0)}</span>
                  </div>
                  {tags.length > 0 && (
                    <div className="vehicle-card-tags">
                      {tags}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="vehicle-inner archive-page">
      <div className="vehicle-header">
        <div>
          <h2>Archive Management</h2>
          <p className="vehicle-subtitle">Comprehensive archive overview and vehicle tracking</p>
        </div>
      </div>

      <div className="vehicle-content">
        <div className="report-container">
          <div className="report-filters">
            <button 
              type="button"
              className={`report-filter-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => { setActiveTab('overview'); setSelectedVehicle(null); setIsRestoreMode(false); setSelectedIds(new Set()); }}
            >
              Overview
            </button>
            <button 
              type="button"
              className={`report-filter-btn ${activeTab === 'stored' ? 'active' : ''}`}
              onClick={() => { setActiveTab('stored'); setSelectedVehicle(null); setIsRestoreMode(false); setSelectedIds(new Set()); }}
            >
              Stored Vehicles
            </button>
            <button 
              type="button"
              className={`report-filter-btn ${activeTab === 'sales' ? 'active' : ''}`}
              onClick={() => { setActiveTab('sales'); setSelectedVehicle(null); setIsRestoreMode(false); setSelectedIds(new Set()); }}
            >
              Sales
            </button>
            <button 
              type="button"
              className={`report-filter-btn ${activeTab === 'workshop' ? 'active' : ''}`}
              onClick={() => { setActiveTab('workshop'); setSelectedVehicle(null); setIsRestoreMode(false); setSelectedIds(new Set()); }}
            >
              Workshop
            </button>
          </div>

          {searchTerm && (
            <div style={{ marginBottom: '20px', padding: '12px', background: 'var(--light-bg)', borderRadius: '8px', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search vehicles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    flex: 1,
                    fontSize: '14px',
                    color: 'var(--text-dark)'
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      color: 'var(--text-muted)'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {(
              <>
                {isRestoreMode ? (
                  <>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Select vehicles to restore to the garage
                    </span>
                    {restorableVehicles.length > 0 && (
                      <>
                        <button
                          type="button"
                          onClick={selectAllRestorable}
                          style={{
                            padding: '8px 12px',
                            fontSize: '12px',
                            border: '1px solid var(--border)',
                            background: 'var(--light-bg)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: 'var(--text-muted)'
                          }}
                        >
                          Select all ({restorableVehicles.length})
                        </button>
                        <button
                          type="button"
                          onClick={clearSelection}
                          style={{
                            padding: '8px 12px',
                            fontSize: '12px',
                            border: '1px solid var(--border)',
                            background: 'var(--light-bg)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: 'var(--text-muted)'
                          }}
                        >
                          Clear
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="hero-button hero-primary"
                      disabled={isRestoringSelected || selectedIds.size === 0}
                      onClick={handleBulkRestore}
                      style={{
                        padding: '8px 16px',
                        fontSize: '12px',
                        opacity: (isRestoringSelected || selectedIds.size === 0) ? 0.6 : 1,
                        cursor: (isRestoringSelected || selectedIds.size === 0) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {restoringIds?.size ? `Restoring…` : `Restore selected (${selectedIds.size})`}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsRestoreMode(false); setSelectedVehicle(null); setSelectedIds(new Set()); }}
                      style={{
                        padding: '8px 16px',
                        fontSize: '12px',
                        border: '1px solid var(--border)',
                        background: 'var(--light-bg)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        color: 'var(--text-muted)'
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="hero-button hero-primary"
                    disabled={isRestoringSelected}
                    onClick={() => setIsRestoreMode(true)}
                    style={{
                      padding: '8px 16px',
                      fontSize: '12px',
                      opacity: isRestoringSelected ? 0.6 : 1,
                      cursor: isRestoringSelected ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isRestoringSelected ? 'Restoring…' : 'Restore'}
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => setSearchTerm(searchTerm ? '' : ' ')}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border)',
                background: 'var(--light-bg)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                color: 'var(--text-muted)'
              }}
            >
              {searchTerm ? 'Clear Search' : 'Search Vehicles'}
            </button>
          </div>

          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'stored' && renderStoredVehicles()}
          {activeTab === 'sales' && renderSalesVehicles()}
          {activeTab === 'workshop' && renderWorkshopVehicles()}
        </div>
      </div>
    </div>
  );
};

export default Archive;





